import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useWindowStorage from 'components/hooks/useWindowStorage';

import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CourseSidePanelForm from 'components/courses/sidepanel/CourseSidePanelForm';
import CourseSidePanelShow from 'components/courses/sidepanel/CourseSidePanelShow';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';
import TrainingRegisterReportsSelector from 'components/trainingRegisterReports/TrainingRegisterReportsSelector';

import { resourceShape } from 'components/helpers/serialisableResources';

const selectableReportTypes = [
  'expiringTraining',
  'completedTraining',
  'currentTrainingStatus',
  'trainingMatrix',
];

export default function CourseSidePanel({
  allowShowFooterContents,
  assignableRoles,
  autoEnrolmentConfiguration,
  closeCallback,
  createCourse,
  currentCourse,
  domainCourse,
  eLearningCourse,
  isLoadMorePersonnelActive,
  isOpen,
  loadMoreRef,
  onCourseCompanyRoleDelete,
  onCourseCompanyRoleRequiredChange,
  onCourseRolesOptionChange,
  onCoursesReset,
  onDeleteClick,
  onELearningProviderSelectedChange,
  onInputChange,
  onOptionChange,
  originalCourse,
  personnel,
  removeErrorStyling,
  requestError,
  setSidePanelContext,
  sidePanelContext,
  submitDisabled,
  updateCourse,
}) {
  const currentActor = useCurrentActor();
  const bodyRef = useRef();

  const { hasPersonnelViewableAccess, isMultiDivisionAccount } =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [getInitialActiveTab, setInitialActiveTab] = useWindowStorage(
    'coursesSidePanelInitialTab',
    { store: window.sessionStorage },
  );

  const initialTabFromStorage = getInitialActiveTab() || 'details';
  const [selectedTab, setSelectedTab] = useState(initialTabFromStorage);

  const [isOverridesRemoveModalOpen, setIsOverridesRemoveModalOpen] =
    useState(false);
  const [
    selectedRequirementPriorityIds,
    setSelectedOverrideRequirementSourceIds,
  ] = useState([]);

  const handleOverrideRemoveModalClose = () => {
    setIsOverridesRemoveModalOpen(false);
    setSelectedOverrideRequirementSourceIds([]);
  };

  const handleOverrideRemoveSuccess = () => {
    resetSidePanel();
    onCoursesReset();
  };

  const handleOverrideRemove = (userCourseId) => {
    setSelectedOverrideRequirementSourceIds((prev) =>
      prev.includes(userCourseId) ? prev : [...prev, userCourseId],
    );
  };

  function handleTabClick(tab) {
    setSelectedTab(tab);
    setInitialActiveTab(tab);
  }

  const openEditCourse = () => {
    setSidePanelContext('edit');
  };

  const resetSidePanel = () => {
    setSelectedTab('details');
    setInitialActiveTab('details');
    setIsOverridesRemoveModalOpen(false);
    setSelectedOverrideRequirementSourceIds([]);
    closeCallback();
  };

  const isReportsTabViewable =
    currentActor.isAllowedFeature('training_register') &&
    hasPersonnelViewableAccess;

  const footerContent = () => {
    switch (sidePanelContext) {
      case 'new':
        return (
          <FormFooter
            onCancel={resetSidePanel}
            onSubmit={createCourse}
            submitButtonDisabled={submitDisabled}
            text={i18nTCommon('courses.addNewCourse')}
          />
        );
      case 'show':
        return (
          allowShowFooterContents &&
          (selectedTab === 'overrides' ?
            selectedRequirementPriorityIds.length > 0 && (
              <FormFooter
                onCancel={() => setSelectedOverrideRequirementSourceIds([])}
                onSubmit={() => setIsOverridesRemoveModalOpen(true)}
                text={i18nTCommon('shared.saveChanges')}
              />
            )
          : <ShowFooter
              deleteDisabled={currentCourse.trainingCount > 0}
              deleteDisabledTooltip={i18nT(
                'show.footer.delete.disabledTooltip',
              )}
              deleteText={i18nTCommon('courses.deleteCourse')}
              editText={i18nTCommon('courses.editCourse')}
              onDeleteClick={onDeleteClick}
              onEditClick={openEditCourse}
            />)
        );
      case 'edit':
        return (
          <FormFooter
            onCancel={resetSidePanel}
            onSubmit={updateCourse}
            submitButtonDisabled={submitDisabled}
            text={i18nTCommon('shared.saveChanges')}
          />
        );
    }
  };

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ?
          domainCourse && (
            <CourseSidePanelShow
              activeTab={selectedTab}
              assignableRoles={assignableRoles}
              autoEnrolmentConfiguration={autoEnrolmentConfiguration}
              currentCourse={currentCourse}
              domainCourse={domainCourse}
              eLearningCourse={eLearningCourse}
              isLoadMorePersonnelActive={isLoadMorePersonnelActive}
              isOverridesRemoveModalOpen={isOverridesRemoveModalOpen}
              isSidePanelOpen={isOpen}
              loadMoreRef={loadMoreRef}
              onOverrideRemove={handleOverrideRemove}
              onOverrideRemoveModalClose={handleOverrideRemoveModalClose}
              onOverrideRemoveSuccess={handleOverrideRemoveSuccess}
              onTabClick={handleTabClick}
              originalCourse={originalCourse}
              personnel={personnel}
              selectedRequirementPriorityIds={selectedRequirementPriorityIds}
            />
          )
        : <CourseSidePanelForm
            assignableRoles={assignableRoles}
            bodyRef={bodyRef}
            currentCourse={currentCourse}
            eLearningCourse={eLearningCourse}
            onCourseCompanyRoleDelete={onCourseCompanyRoleDelete}
            onCourseCompanyRoleRequiredChange={
              onCourseCompanyRoleRequiredChange
            }
            onCourseRolesOptionChange={onCourseRolesOptionChange}
            onELearningProviderSelectedChange={
              onELearningProviderSelectedChange
            }
            onInputChange={onInputChange}
            onOptionChange={onOptionChange}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
          />
      }
      bodyRef={bodyRef}
      closeCallback={resetSidePanel}
      color='tertiary'
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footerContent()}
      headerContent={
        <>
          <div className='popup__title popup__title--tertiary'>
            <h1
              className={classNames(
                sidePanelContext === 'show' ?
                  'tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-500'
                : 'tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900',
              )}
            >
              {sidePanelContext === 'new' ?
                i18nTCommon('courses.addNewCourse')
              : sidePanelContext === 'show' ?
                i18nTCommon('shared.course')
              : sidePanelContext === 'edit' && i18nTCommon('courses.editCourse')
              }
            </h1>
          </div>
          {sidePanelContext === 'show' && (
            <div>
              <h2
                className={classNames(
                  'tw-mt-2 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-700',
                  isReportsTabViewable ? 'tw-mb-4' : 'tw-mb-0',
                )}
              >
                {currentCourse.name}
              </h2>
              {isReportsTabViewable && (
                <div>
                  <TrainingRegisterReportsSelector
                    filter={{ courseId: { eq: currentCourse.id } }}
                    selectableReportTypes={selectableReportTypes}
                  />
                </div>
              )}
            </div>
          )}
          {sidePanelContext === 'edit' && isMultiDivisionAccount && (
            <p className='tw-m-0 tw-mt-5 tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
              {i18nT('edit.header.multiDivisionAccountWarning')}
            </p>
          )}
        </>
      }
      isOpen={isOpen}
      scrollHeader={false}
      submitDisabled={submitDisabled}
    />
  );
}

CourseSidePanel.propTypes = {
  allowShowFooterContents: PropTypes.bool.isRequired,
  currentCourse: PropTypes.object.isRequired,
  domainCourse: resourceShape('course').isRequired,
  originalCourse: resourceShape('originalCourse'),
  eLearningCourse: resourceShape('eLearningCourse'),
  autoEnrolmentConfiguration: resourceShape('autoEnrolmentConfiguration'),
  requestError: PropTypes.object.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeCallback: PropTypes.func.isRequired,
  sidePanelContext: PropTypes.string.isRequired,
  setSidePanelContext: PropTypes.func.isRequired,
  updateCourse: PropTypes.func.isRequired,
  createCourse: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onELearningProviderSelectedChange: PropTypes.func.isRequired,
  onCoursesReset: PropTypes.func.isRequired,
  assignableRoles: PropTypes.array.isRequired,
  isLoadMorePersonnelActive: PropTypes.bool.isRequired,
  personnel: PropTypes.object.isRequired,
  onCourseCompanyRoleRequiredChange: PropTypes.func.isRequired,
  onCourseCompanyRoleDelete: PropTypes.func.isRequired,
  onCourseRolesOptionChange: PropTypes.func.isRequired,
};
